import { formatCurrencyAmount } from '@modules/Orders/shared/hooks/useCurrency';
import { Badge } from '@shared/components/ui/badge';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CourierFilePayment } from '../models';

export const useAgGridColumns = () => {
  const { t } = useTranslation('codCourierFileDetail');

  const columnDefs: ColDef<CourierFilePayment>[] = useMemo(
    () => [
      {
        field: 'order.ref',
        headerName: t('payments.table.order', 'Pedido'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.ref ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm font-medium text-foreground">{params.data?.order?.ref ?? '-'}</span>
        ),
      },
      {
        field: 'order_id',
        headerName: t('payments.table.order_id', 'ID Pedido'),
        minWidth: 100,
        sortable: false,
        valueGetter: (params) => params.data?.order_id ?? params.data?.order?.id ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.order_id ?? params.data?.order?.id ?? '-'}</span>
        ),
      },
      {
        field: 'tracking_number',
        headerName: t('payments.table.tracking', 'Tracking'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.tracking_number ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.tracking_number ?? '-'}</span>
        ),
      },
      {
        field: 'client.name',
        headerName: t('payments.table.client', 'Cliente'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.client?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-foreground">{params.data?.client?.name ?? '-'}</span>
        ),
      },
      {
        field: 'order.shop.name',
        headerName: t('payments.table.shop', 'Tienda'),
        minWidth: 150,
        sortable: false,
        valueGetter: (params) => params.data?.order?.shop?.name ?? '-',
        cellRenderer: (params: any) => (
          <span className="text-sm text-muted-foreground">{params.data?.order?.shop?.name ?? '-'}</span>
        ),
      },
      {
        field: 'original_amount',
        headerName: t('payments.table.original_amount', 'Importe Original'),
        minWidth: 150,
        sortable: true,
        valueGetter: (params) => params.data?.original_amount ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.original_amount ?? 0;
          const currency = params.data?.currency ?? 'EUR';
          return <span className="text-sm font-medium text-foreground">{formatCurrencyAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'effective_amount',
        headerName: t('payments.table.effective_amount', 'Importe Efectivo'),
        minWidth: 150,
        sortable: true,
        valueGetter: (params) => params.data?.effective_amount ?? 0,
        cellRenderer: (params: any) => {
          const amount = params.data?.effective_amount ?? 0;
          const currency = params.data?.currency ?? 'EUR';
          return <span className="text-sm font-medium text-foreground">{formatCurrencyAmount(amount, currency)}</span>;
        },
      },
      {
        field: 'status',
        headerName: t('payments.table.status', 'Estado'),
        minWidth: 130,
        sortable: false,
        valueGetter: (params) => params.data?.status ?? '-',
        cellRenderer: (params: any) => {
          const status = params.data?.status;
          if (!status) return '-';

          // Normalizar el estado a mayúsculas para la comparación
          const normalizedStatus = status.toUpperCase();

          const variants: Record<string, { variant: any; label: string }> = {
            PENDING: {
              variant: 'warning',
              label: t('payments.status.pending', 'Pendiente'),
            },
            PAID: {
              variant: 'success',
              label: t('payments.status.paid', 'Pagado'),
            },
            UNPAID: {
              variant: 'warning',
              label: t('payments.status.unpaid', 'No Pagado'),
            },
            PROCESSED: {
              variant: 'success',
              label: t('payments.status.processed', 'Procesado'),
            },
          };

          const statusInfo = variants[normalizedStatus] || { variant: 'default', label: status };

          return <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>;
        },
      },
    ],
    [t],
  );

  return { columnDefs };
};
